"use client";

import * as React from "react";
import { computeMarqueeLayout, type MarqueeLayout } from "./marquee.measure";

/** Measures the track and returns the animation duration (s), the number of
 *  content copies needed to fill the container, and whether one copy already
 *  fits — in which case there is nothing to scroll.
 *
 *  `assumeFits` seeds the state for the first paint, before any measurement has
 *  run: pass `true` where a static row is the expected resting state, so the
 *  page doesn't flash a row of duplicates on its way there. */
export function useMarqueeDuration(
  trackRef: React.RefObject<HTMLDivElement | null>,
  speed: number,
  assumeFits = false,
): MarqueeLayout {
  const [layout, setLayout] = React.useState<MarqueeLayout>({
    fits: assumeFits,
    duration: 0,
    numCopies: assumeFits ? 1 : 3,
  });

  React.useEffect(() => {
    const node = trackRef.current;
    if (!node) return;
    const container = node.parentElement;

    const compute = () => {
      const copy = node.children[0] as HTMLElement | undefined;
      if (!copy) return;

      const copyRect = copy.getBoundingClientRect();
      const nextCopy = node.children[1] as HTMLElement | undefined;
      const next = computeMarqueeLayout({
        copyWidth: copyRect.width,
        containerWidth: container?.clientWidth ?? 0,
        stride: nextCopy
          ? nextCopy.getBoundingClientRect().left - copyRect.left
          : copyRect.width,
        speed,
      });

      if (!next) return;
      setLayout((current) =>
        current.fits === next.fits &&
        current.duration === next.duration &&
        current.numCopies === next.numCopies
          ? current
          : next,
      );
    };

    compute();

    const observer = new ResizeObserver(compute);
    // A `w-max` track ignores the window; a `w-full` one ignores its content.
    observer.observe(node);
    if (container) observer.observe(container);
    const firstCopy = node.children[0];
    if (firstCopy) observer.observe(firstCopy);
    return () => observer.disconnect();
  }, [trackRef, speed]);

  return layout;
}
